<template>
	<v-card class="ma-2 full-height-card">
		<v-card-title class="py-4">
			<div>
				<v-text-field
					v-model="search"
					:label="$t('search')"
					append-icon="mdi-magnify"
					single-line
					hide-details
					outlined
					dense
					clearable />
			</div>
		</v-card-title>
		<v-card-text class="overflow-y-auto" style="height: calc(100% - 72px)">
			<v-data-table
				:headers="headers"
				:items="schedulesByLocationId"
				v-model="selectedSchedules"
				:search="search"
				dense
				show-expand
				single-expand
				:expanded.sync="expandedRows"
				:height="pageHeight(124)"
				:loading="loadingSchedules"
				:items-per-page="-1"
				class="data-table-outline">
				<template #item="{ item, expand, isExpanded, index }">
					<tr @click="expand(!isExpanded)" :class="getRowsForDataTable(index)" style="cursor: pointer">
						<td class="text-left">{{ item.scheduleRuleName }}</td>
						<td class="text-left">{{ item.scheduleCreatedByName }}</td>
						<td class="text-left">{{ item.scheduleOwnerName }}</td>
						<td class="text-left">{{ formatTimestamp(item.scheduleStartDate) }}</td>
						<td class="text-right">
							<v-icon small>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
						</td>
					</tr>
				</template>
				<template #expanded-item="{ item, headers }">
					<td :colspan="headers.length" class="pa-3">
						<v-data-table
							dense
							:headers="subHeaders"
							:items="item.scheduleOccurrences"
							hide-default-footer
							:items-per-page="-1"
							:height="pageHeight(-150)"
							class="data-table-outline overflow-y-auto">
							<template #item="{ item: subItem, index }">
								<tr @click="selectSchedule(item, subItem)" :class="getRowsForDataTable(index)" class="text-left">
									<td class="px-2">{{ subItem.occurrenceName }}</td>
									<td class="truncate-text px-2" style="max-width: 30vw">
										{{ subItem.occurrenceDescription || $t('none') }}
									</td>
									<td class="px-2">{{ formatTimestamp(new Date(subItem.occurrenceStartAt), 'datetime_med') }}</td>
									<td class="px-2">{{ formatTimestamp(new Date(subItem.occurrenceEndAt), 'datetime_med') }}</td>
									<td class="px-2">{{ formatDuration(subItem.occurrenceDuration * 60) }}</td>
									<td class="px-2">{{ subItem.frequency }}</td>
									<td class="px-2">{{ subItem.interval || $t('none') }}</td>
									<td class="px-2">
										{{ item.scheduleTasksDetail?.filter((task) => task.occurrenceId === subItem.id)?.length || '0' }}
									</td>
								</tr>
							</template>
						</v-data-table>
					</td>
				</template>
			</v-data-table>
		</v-card-text>
		<v-dialog persistent width="auto" v-model="dialog" v-if="dialog">
			<v-card style="min-height: 50vh; max-height: 80vh; width: 90vw; min-width: 600px" class="d-flex flex-column">
				<v-card-title class="card-header fixed-card-title2 d-flex justify-space-between">
					{{ $t('tasks') }}
					<v-icon @click="dialog = false" class="white-icon">mdi-close</v-icon>
				</v-card-title>
				<v-card-text class="pa-0 overflow-auto" style="width: 90vw; min-width: 600px">
					<v-data-table dense :headers="dialogHeaders" :items="selectedTasks" hide-default-footer :items-per-page="-1">
						<template #item="{ item, index }">
							<tr class="text-left" :class="getRowsForDataTable(index)">
								<td>{{ item.taskDetailDescription }}</td>
								<td>{{ formatTimestamp(item.plannedStartAt, 'datetime_med') }}</td>
								<td>{{ formatTimestamp(item.taskActualStartAt, 'datetime_med') }}</td>
								<td>{{ formatDuration(item.plannedDuration * 60) }}</td>
								<td>{{ item.locationName }}</td>
								<td>{{ item.taskExecutorByName }}</td>
							</tr>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { formatDuration, formatTimestamp, getRowsForDataTable } from '../../../utils/utilities';

export default {
	data () {
		return {
			loadingSchedules: false,
			selectedSchedules: [],
			expandedRows: [],
			search: '',
			dialog: false,
			selectedTasks: null,
		};
	},
	computed: {
		...mapState({
			hospitalAssignedData: (state) => state.authentication.userData.hospitalAssignedData,
			schedulesByLocationId: (state) =>
				state.schedules.schedulesByLocationId.sort((a, b) => {
					const nameA = a.scheduleRuleName.toLowerCase();
					const nameB = b.scheduleRuleName.toLowerCase();
					if (nameA < nameB) return -1;
					if (nameA > nameB) return 1;
					return 0;
				}),
		}),
		...mapGetters({ pageHeight: 'pageHeight' }),
		headers () {
			return [
				{ text: this.$t('schedule'), value: 'scheduleRuleName' },
				{ text: this.$t('created-by'), value: 'scheduleCreatedByName' },
				{ text: this.$t('coordinator'), value: 'scheduleOwnerName' },
				{ text: this.$t('start-date'), value: 'scheduleStartDate' },
				{ text: '', value: 'data-table-expand' },
			];
		},
		subHeaders () {
			return [
				{ text: this.$t('task-list-name'), value: 'occurrenceName', class: 'px-2' },
				{ text: this.$t('description'), value: 'occurrenceDescription', class: 'px-2' },
				{ text: this.$t('start-at'), value: 'occurrenceStartAt', class: 'px-2' },
				{ text: this.$t('end-at'), value: 'occurrenceEndAt', class: 'px-2' },
				{ text: this.$t('duration'), value: 'occurrenceDuration', class: 'px-2' },
				{ text: this.$t('frequency'), value: 'frequency', class: 'px-2' },
				{ text: this.$t('interval'), value: 'interval', class: 'px-2' },
				{ text: this.$t('task-count'), value: 'count', class: 'px-2' },
			];
		},
		dialogHeaders () {
			return [
				{ text: this.$t('description'), value: 'taskDetailDescription' },
				{ text: this.$t('planned-start-at'), value: 'plannedStartAt' },
				{ text: this.$t('execution-start-at'), value: 'taskActualStartAt' },
				{ text: this.$t('planned-duration'), value: 'plannedDuration' },
				{ text: this.$t('location-name'), value: 'locationName' },
				{ text: this.$t('staff-name'), value: 'taskExecutorByName' },
			];
		},
		browserTimeZone () {
			return Intl.DateTimeFormat().resolvedOptions().timeZone;
		},
	},
	async mounted () {
		this.loadSchedulesByLocationId();
	},
	methods: {
		formatTimestamp,
		formatDuration,
		getRowsForDataTable,
		...mapActions({
			getSchedulesByLocationId: 'schedules/getSchedulesByLocationId',
		}),
		async loadSchedulesByLocationId () {
			this.loadingSchedules = true;
			const selectedHospitalAssignedId = JSON.parse(localStorage.getItem('selectedHospitalAssignedId'));
			await this.getSchedulesByLocationId(selectedHospitalAssignedId);
			this.loadingSchedules = false;
		},
		selectSchedule (item, subItem) {
			this.selectedTasks = item.scheduleTasksDetail.filter((s) => s.occurrenceId === subItem.id);
			this.dialog = true;
		},
	},
	watch: {},
};
</script>
<style scoped>
.full-height-card {
	height: calc(100vh - 77px) !important;
}
</style>
